import {
  getConfigByApp,
  isUserAgentForBot,
  removedUndefinedFromArrayObject,
  utilServerSideDeviceDetection,
} from '@utils';
import {metaInfo} from '@configs';
import {COMPANY, COMPANY_CAREER, JOBDETAIL, JOBFEED} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {wrapper} from '@jobseeker/common-core/stores';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import loadAds from '@jobseeker/common-core/helpers/loadAds';
import {Interceptor} from '@jobseeker/common-core/components';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import Company from '@layouts/Body/Company';
import {
  jobDetailService,
  jobfeedService,
} from '@jobseeker/common-core/services';
import type {GetServerSidePropsContext} from 'next/types';
import {AppState} from '@jobseeker/common-core/types';
import {InjectSearchOverlay} from '@components';
import {useEffect} from 'react';
import {TrackEventUBA} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import dynamic from 'next/dynamic';
import {isCSR} from '@jobseeker/lotus/utils';
const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);
type CompanyPageProps = {
  seoConfig: object;
  companyId: string | undefined;
  ambitionBoxData: ABdataProps;
  slug: string | undefined;
  pageNo: number;
  companyJobs: {
    jobs: [];
    hasMore: false;
  };
  companyRefCode?: string;
};

type ABdataProps = {
  ambitionboxCompanyId: number;
  benefits: {
    totalBenefits: number;
    benefits: [
      {
        name: string;
        iconUrl: string;
        count: number;
        totalCount: number;
      },
    ];
    benefitsUrl: string;
    usersReportedBenefits: number;
  };
  companyInfo: {
    ambitionBoxCompanyId: number;
    companyName: string;
    reviewsCount: number;
    companyLogo: string;
    aboutCompany: string;
    foundedYear: string;
    totalEmployees: string;
    totalEmployeesIndia: string;
    companyType: string;
    primaryIndustry: string;
    natureOfBusiness: [string];
    hq: string;
    officeLocations: [
      {
        cityName: string;
      },
    ];
    otherTags: [string];
    title: string;
    url: string;
    aggregateRating: number;
    aggregateRatingOneDecimal: number;
  };
  topJobProfiles: {
    entities: [
      {
        name: string;
        url: string;
        count: number;
        rating: number;
      },
    ];
    topJobProfilesUrl: string;
  };
};

const CompanyPage = ({
  seoConfig,
  ambitionBoxData,
  companyId,
  slug,
  pageNo,
  companyJobs,
  companyRefCode,
}: CompanyPageProps) => {
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, JOBDETAIL);
  const router = useRouter();
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();
  const onLoginSuccess = () => {
    if (isCSR()) {
      window.location.reload();
    }
  };

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: `${ambitionBoxData?.companyInfo?.companyName} Careers`,
      item: `/${slug}-careers`,
    },
  ];

  useValidateAuth(dispatch);
  const isSearchEnabled = useSelector(
    (state: AppState) => state.app.layout.searchOverlay,
    shallowEqual
  );
  const {enableLoginPopup} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );
  useEffect(() => {
    TrackEventUBA('companyCareerPageView', 'view', {
      ref: router.query.ref || '',
      filterExp: router.query.minexp
        ? `${router.query.minexp}-${router.query.maxexp}`
        : '',
      filterLocation: router.query.loc || '',
      filterCategory: router.query.cat || '',
      sessionUsername: `${sessionStorage.getItem('userId') || ''}`,
    });
  }, []);
  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout seoConfig={seoConfig} paginationEnabled={true} />
      <NavLayout showbottom={true}>
        <ResponsiveLayout
          breadcrumbElements={breadcrumbElements}
          variant={VersionVariants.VERSION_TWO}
        >
          <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
          <HiristAuthentication
            variant={VersionVariants.VERSION_TWO}
            onSuccess={onLoginSuccess}
            openDialog={enableLoginPopup}
            setOpenDialog={() => {
              dispatch({
                type: 'AppContext/set_login_popup',
                payload: false,
              });
            }}
          />

          <Company
            ambitionBoxData={ambitionBoxData}
            companyId={companyId}
            slug={slug}
            pageNo={pageNo}
            companyJobs={companyJobs}
            companyRefCode={companyRefCode}
          />
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};
export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const url = context?.resolvedUrl || '';

    const metaData = getConfigByApp(metaInfo, JOBFEED, true, JOBDETAIL);

    const {
      header: {
        meta: {},
      },
      ads: {learnGrow, featuredInstitutes},
    } = metaData || {};

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: COMPANY},
    });
    let slug = context.params?.companySlug || context.query?.companySlug;
    slug = encodeURIComponent(slug);

    const pageParam = Array.isArray(context.query?.page)
      ? context.query?.page[0]
      : context.query?.page;
    const parsedPage = parseInt((pageParam as string) || '1', 10);
    const pageNo =
      Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;

    // const ambitionBoxResp = await jobDetailService.getAmbitionBoxData(
    //   slug.split('-').slice(0, -1).join('-')
    // );
    const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
    const userIp = context.req?.headers?.['x-forwarded-for']
      ?.toString()
      .split(',')[0];
    const headers = {
      'user-agent': context.req?.headers?.['user-agent'],
      'x-forwarded-for': userIp,
      referer: `https://www.${process.env.NEXT_PUBLIC_APP_DOMAIN}/`,
    };
    const ambitionBoxResp = await jobDetailService.getAmbitionBoxData(
      slug,
      headers
    );

    const ambitionBoxData = ambitionBoxResp.data;
    const companyId = ambitionBoxData?.centralisedCompanyId || -1;
    const companyRefCode = ambitionBoxData?.data?.refCode ?? '';

    const isBotUser = isUserAgentForBot(context);
    let com_jobs: any = null;

    if (isBotUser) {
      com_jobs = await jobfeedService.getCompanyJobs(
        slug || '',
        pageNo - 1,
        undefined,
        undefined,
        headers
      );

      try {
        const jobFeeds: any = await jobfeedService.getCompanySimilarJobs(
          0,
          headers
        );

        jobFeeds.totalJobs = jobFeeds?.jobs?.length;
        store.dispatch({
          type: 'job/jobFeed/set_jobfeed',
          payload: jobFeeds,
        });
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:200 ~ store jobfeed category jobs=> ~ error:',
          error
        );
      }

      try {
        if (learnGrow?.slot) {
          const ads = await loadAds(
            learnGrow.slot,
            {
              categoryIds: '',
              page: 0,
              size: 20,
              user_location: 0,
              shuffle: 1,
              orderby: 'modified',
            },
            true
          );
          if (ads) {
            store.dispatch({
              type: 'Ads/set_learn_grow',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (featuredInstitutes?.slot) {
          const ads = await loadAds(featuredInstitutes.slot, {
            categoryIds: '',
          });

          if (ads) {
            store.dispatch({
              type: 'Ads/set_featured_institutes',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:277 ~ store=> ~ job details Ads API error:',
          error
        );
      }
    }
    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }
    const compName = ambitionBoxData?.companyInfo?.companyName || 'company';
    const totalCompanyJobs = com_jobs?.count > 0 ? com_jobs.count : '';
    const seoConfig = await loadSEO(
      COMPANY_CAREER,
      {
        title: `${compName} Careers, ${totalCompanyJobs} ${compName} Jobs, ${new Date().toLocaleString(
          'en-US',
          {
            month: 'long',
            year: 'numeric',
          }
        )}`,
        description: `Apply to ${totalCompanyJobs} ${compName} jobs on ${
          isAppIimjobs ? 'iimjobs.com' : 'Hirist.tech'
        }, India's No.1 ${
          isAppIimjobs ? 'Management' : 'IT'
        } job portal. Explore ${
          isAppIimjobs ? 'Management' : 'IT'
        } job in ${compName} Now!`,
      },
      `/${url?.split('?')[0]?.split('/')[2]}-careers`,
      true,
      pageNo
    );

    if (seoConfig && typeof seoConfig === 'object') {
      const baseCanonical = (seoConfig as any).canonical;
      if (baseCanonical) {
        (seoConfig as any).canonical =
          pageNo > 1 ? `${baseCanonical}?page=${pageNo}` : baseCanonical;

        const paginationTags: {rel: string; href: string}[] = [];
        if (pageNo > 1) {
          paginationTags.push({
            rel: 'prev',
            href:
              pageNo - 1 <= 1
                ? baseCanonical
                : `${baseCanonical}?page=${pageNo - 1}`,
          });
        }
        if (com_jobs?.data?.hasMore) {
          paginationTags.push({
            rel: 'next',
            href: `${baseCanonical}?page=${pageNo + 1}`,
          });
        }
        const existingTags = Array.isArray(
          (seoConfig as any).additionalLinkTags
        )
          ? (seoConfig as any).additionalLinkTags.filter(
              (tag: {rel?: string}) =>
                tag?.rel !== 'prev' && tag?.rel !== 'next'
            )
          : [];
        (seoConfig as any).additionalLinkTags = [
          ...existingTags,
          ...paginationTags,
        ];
      }
    }
    if (
      !companyId ||
      companyId == '' ||
      companyId == undefined ||
      !ambitionBoxData ||
      Object.keys(ambitionBoxData).length === 0
    ) {
      return {
        notFound: true,
      };
    }
    return {
      props: {
        seoConfig,
        metaData,
        companyId: companyId || '',
        ambitionBoxData: ambitionBoxData,
        slug: slug,
        pageNo,
        companyJobs: {
          jobs: com_jobs?.data?.jobs || [],
          hasMore: com_jobs?.data?.hasMore || false,
        },
        companyRefCode,
      },
    };
  }
);

export default CompanyPage;
